import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import axios from 'axios'
import { useLocation } from 'react-router-dom'
import LogoImage from '../../atoms/Utlis/LogoImage'
import LoadingDots from '../../atoms/Utlis/LoadinDots'
import { getHostUrl } from '../../../Redux/ApiCalls/getHostUrl'

type DeeplinkState = 'loading' | 'ready' | 'missingParams' | 'error'

const DeeplinkRedirection = () => {
  const location = useLocation()
  const [code, setCode] = useState('')
  const [status, setStatus] = useState<DeeplinkState>('loading')

  const { businessId, codeFromUrl } = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return {
      businessId: params.get('businessId') ?? params.get('business_id') ?? '',
      codeFromUrl: params.get('code') ?? ''
    }
  }, [location.search])

  useEffect(() => {
    if (!codeFromUrl || !businessId) {
      setStatus('missingParams')
      setCode(codeFromUrl ?? '')
      return
    }

    setCode(codeFromUrl)
    let isMounted = true

    const fetchDeeplink = async () => {
      try {
        setStatus('loading')
        const response = await axios.get(`${getHostUrl()}/account/deeplink/`, {
          params: { business_id: businessId }
        })

        if (!isMounted) return
        console.log('deeplink', response?.data?.data?.deeplink ,codeFromUrl )
        const deeplink =
          response?.data?.deeplink ||
          response?.data?.deeplink_url ||
          response?.data?.data?.deeplink ||
          response?.data?.data?.deeplink_url

        if (deeplink) {
          let redirectUrl = ""
          if (deeplink.includes("code=")) {
            redirectUrl = `${deeplink}${codeFromUrl}`
          } else {
            const separator = deeplink.includes("?") ? "&" : "?"
            redirectUrl = `${deeplink}${separator}code=${codeFromUrl}`
          }

          if (redirectUrl.includes("onelink.me") && !redirectUrl.includes("deep_link_value=")) {
            const encoded = encodeURIComponent(codeFromUrl)
            redirectUrl += `&c=album-redeem&deep_link_value=album-redeem&deep_link_sub1=${encoded}&af_sub1=${encoded}`
          }

          console.log("redirectUrl", redirectUrl)
          window.location.replace(redirectUrl)
          return
        }

        setStatus('ready')
      } catch (error) {
        if (!isMounted) return
        setStatus('error')
      }
    }

    fetchDeeplink()

    return () => {
      isMounted = false
    }
  }, [businessId, codeFromUrl])

  return (
    <PageWrapper>
      {
        status === "loading" ? <LoadingDots />:  <Card>
        <LogoWrapper>
          <LogoImage />
        </LogoWrapper>
        <Title>Scan the QR from the KwikMemories app</Title>
        <Description>
          {!code
            ? 'We could not find a valid event code. Please contact your studio for a fresh QR code.'
            : 'This studio has not set up a custom deeplink yet. To continue, open the KwikMemories mobile app and scan the QR shared by your studio owner or enter the code shown below.'}
        </Description>

        {!!code && (
          <>
            <HelperLabel>Your event code</HelperLabel>
            <CodeBadge>{code}</CodeBadge>
          </>
        )}

      
        {status === 'error' && (
          <StatusText $error>
            We couldn&apos;t reach the studio right now. Please retry in a few seconds or contact your studio owner.
          </StatusText>
        )}
        {status === 'missingParams' && (
          <StatusText $error>
            Missing details in the link you opened. Request a fresh link from your studio owner.
          </StatusText>
        )}
        <SecondaryText>
          Need the app?{' '}
          <ExternalLink href="https://kwikmemories.com/download" target="_blank" rel="noreferrer">
            Download KwikMemories
          </ExternalLink>
        </SecondaryText>
      </Card>
      }
     
    </PageWrapper>
  )
}

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f7f7f7;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`

const Card = styled.div`
  width: 100%;
  max-width: 520px;
  background: #ffffff;
  border-radius: 28px;
  padding: 40px 32px;
  box-shadow: 0 12px 48px rgba(15, 15, 15, 0.08);
  text-align: center;
`

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
`

const Title = styled.h1`
  font-size: 24px;
  line-height: 32px;
  margin-bottom: 16px;
  color: #171717;
`

const Description = styled.p`
  color: #565656;
  line-height: 24px;
  margin-bottom: 24px;
`

const HelperLabel = styled.p`
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  color: #8b8b8b;
`

const CodeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  border-radius: 999px;
  background: linear-gradient(144deg, #ffc951 0%, #c94daa 130%);
  color: #ffffff;
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 24px;
`

const StatusText = styled.p<{ $error?: boolean }>`
  color: ${(props) => (props.$error ? '#e74c3c' : '#8b8b8b')};
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 18px;
`

const LoaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
`

const SecondaryText = styled.p`
  color: #565656;
  font-size: 14px;
`

const ExternalLink = styled.a`
  color: #c94daa;
  font-weight: 600;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`

export default DeeplinkRedirection